import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button3D from '../../ui/Button3D';
import { DEFAULT_EVENT_WORDS } from '../eventWords';
import GroupTransfersView from '../../dispatch/groupTransfers/GroupTransfersView';
import GuestTransportPopup from './GuestTransportPopup';
import { friendlyTransportError, loadGuestTransport, requestGuestTransport } from './guestTransportApi';
import {
    cannotRequestReason, newRequestId, transportFields, transportHeadline, tripStatusLabel, tripStatusTone, tripWhen,
} from './guestTransport';

const TONE_BORDER = { green: 'border-[#4ADE80]', amber: 'border-[#FACC15]', grey: 'border-[#94A3B8]' };

// Module 19 (BL-380) G9a - "Guest Transport" on the event's Guests & RSVP screen. Shown only when the shuttle question is on (Events
// Settings › Hotel & Travel). With the Dispatch pack in the same company account, an Events editor who also has Dispatch rights turns
// the shuttle needs into ordinary Dispatch trips (requested; Dispatch confirms); everyone who can see the event sees those trips and
// their Dispatch status. Without the Dispatch pack, one line says so and nothing is created.
export default function GuestTransportPanel({ client, engagementId, showShuttle = true, shuttle = null, words = DEFAULT_EVENT_WORDS }) {
    const [loaded, setContext] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    // one request id per opened popup: a retry after a network hiccup can never make a second trip
    const requestId = useRef(null);

    const refresh = useCallback(async () => {
        try {
            setContext(await loadGuestTransport(client, engagementId));
            setLoadError('');
        } catch (problem) {
            setLoadError(friendlyTransportError(problem));
        }
    }, [client, engagementId]);

    useEffect(() => {
        if (!showShuttle) return undefined;
        let alive = true;
        loadGuestTransport(client, engagementId)
            .then((value) => { if (alive) setContext(value); })
            .catch((problem) => { if (alive) setLoadError(friendlyTransportError(problem)); });
        return () => { alive = false; };
    }, [client, engagementId, showShuttle]);

    // the live count from the guest list on screen (the database count is only as fresh as the last read)
    const context = loaded && Number.isInteger(shuttle) ? { ...loaded, shuttleGuests: shuttle } : loaded;
    if (!showShuttle) return null;
    if (loadError) return <p role="alert" className="ia-resv-error" data-testid="guest-transport-error">{loadError}</p>;
    if (!context || context.shuttleEnabled === false) return null;
    if (!context.dispatchEnabled) {
        return (
            <p className="text-sm" data-testid="guest-transport-note">
                Guest Transport needs the Dispatch pack in this company account, so shuttle trips cannot be requested from here.
            </p>
        );
    }

    const trips = context.trips;
    const reason = cannotRequestReason(context);
    const openPopup = () => { requestId.current = newRequestId(); setError(''); setNotice(''); setOpen(true); };
    const save = async (form) => {
        setSaving(true); setError('');
        try {
            const result = await requestGuestTransport(client, engagementId, requestId.current, transportFields(form, context.companies));
            setOpen(false);
            setNotice(`${result.confirmationNumber ? `Trip ${result.confirmationNumber}` : 'The trip'} was requested. It shows in Dispatcher once Dispatch confirms it.`);
            await refresh();
        } catch (problem) {
            setError(friendlyTransportError(problem));
        }
        setSaving(false);
    };

    return (
        <section className="ia-d2-card space-y-2" aria-label="Guest Transport" data-testid="guest-transport">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                    <h3 className="text-sm font-bold uppercase tracking-wide">Guest Transport</h3>
                    <div className="break-words text-sm" data-testid="guest-transport-headline">{transportHeadline(trips, context.companies)}</div>
                </div>
                {context.canRequest && <Button3D variant="pill" onClick={openPopup} data-testid="guest-transport-request">Request a trip</Button3D>}
            </div>
            <div className="text-sm" data-testid="guest-transport-need">
                {context.shuttleGuests} {Number(context.shuttleGuests) === 1 ? 'guest needs' : 'guests need'} the shuttle, plus-ones included.
            </div>
            {reason && <p className="text-sm" data-testid="guest-transport-reason">{reason}</p>}
            {notice && <p className="text-sm font-semibold" role="status" data-testid="guest-transport-notice">{notice}</p>}
            {trips.length === 0 ? (
                <div className="text-sm" data-testid="guest-transport-empty">No trips requested yet.</div>
            ) : (
                <ul className="divide-y divide-[#26365A]" aria-label="Shuttle trips" data-testid="guest-transport-trips">
                    {trips.map((trip) => (
                        <li key={trip.id} className="flex flex-wrap items-start justify-between gap-2 py-2" data-testid="guest-transport-trip" data-trip-id={trip.id}>
                            <div className="min-w-0 text-sm">
                                <div className="font-semibold">{tripWhen(trip.scheduledStart, context.timeZone)}</div>
                                <div className="break-words">{trip.pickup} → {trip.dropoff}</div>
                                <div>{trip.passengers} {Number(trip.passengers) === 1 ? 'passenger' : 'passengers'}{trip.service ? ` · ${trip.service}` : ''}{trip.confirmationNumber ? ` · ${trip.confirmationNumber}` : ''}</div>
                            </div>
                            <span
                                className={`inline-flex items-center rounded-full border ${TONE_BORDER[tripStatusTone(trip.status)]} px-3 py-1 text-sm font-bold`}
                                data-testid="guest-transport-status"
                                data-status={trip.status}
                            >
                                {tripStatusLabel(trip.status)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            <GroupTransfersView client={client} eventId={engagementId} embedded />
            {open && <GuestTransportPopup context={context} words={words} saving={saving} error={error} onSave={save} onClose={() => setOpen(false)} />}
        </section>
    );
}
